import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useReveal from "../Components/useReveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faHandHoldingHeart,
  faUserGroup,
  faLeaf,
  faPhone,
  faBullseye,
  faEye,
  faCheck,
  faCalendarCheck,
  faStar,
  faShieldHeart,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import "../Styles/AboutPage.css";
import aboutImg from "../Images/about.jpg";
import whyImg from "../Images/image1.jpg";

const values = [
  {
    icon: faHandHoldingHeart,
    title: "Compassionate Care",
    text: "We treat every client with empathy and kindness, ensuring they feel valued and respected in their daily lives.",
  },
  {
    icon: faUserGroup,
    title: "Personalized Service",
    text: "Our approach is tailored to each individual's unique needs, providing care that truly enhances quality of life.",
  },
  {
    icon: faLeaf,
    title: "Sustainability",
    text: "We nurture both our clients' wellbeing and the environment, promoting sustainability across our services.",
  },
];

const whyPoints = [
  {
    icon: faStar,
    title: "5-Star Rated Service",
    text: "Consistently praised by families for exceptional, caring support.",
  },
  {
    icon: faShieldHeart,
    title: "Compassionate Professionals",
    text: "Empathetic caregivers who genuinely care about your well-being.",
  },
  {
    icon: faHandHoldingHeart,
    title: "Tailored Care Plans",
    text: "Every plan is built around your unique needs and preferences.",
  },
  {
    icon: faClock,
    title: "Flexible Availability",
    text: "Support available between 6:00 AM and 10:30 PM.",
  },
];

function About() {
  useReveal();

  return (
    <div className="about-page">
      <Navbar />

      <section className="page-banner">
        <div className="hr-container">
          <span className="hr-eyebrow">About Us</span>
          <h1>Caring for You</h1>
          <p>
            Empowering lives with compassionate, nonmedical in-home care that
            puts independence and dignity first.
          </p>
          <div className="page-crumb">
            <Link to="/">Home</Link> / About
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="hr-section">
        <div className="about-intro hr-container">
          <div className="about-intro-visual reveal">
            <img
              src={aboutImg}
              alt="Caregiver supporting a client"
              className="abt-photo"
            />
            <div className="abt-ribbon">
              <FontAwesomeIcon icon={faHeart} /> Empowering lives with compassion
            </div>
          </div>
          <div className="about-intro-body reveal">
            <span className="hr-eyebrow">Authentic Care</span>
            <h2 className="hr-heading">
              Empowering Lives with <span className="accent">Compassion</span>
            </h2>
            <p>
              At Harmony Rise Homecare, we specialize in delivering exceptional
              nonmedical in-home care services. Our focus is on promoting
              independence and enhancing the quality of life for our clients
              through personalized and compassionate care.
            </p>
            <p>
              Founded with a mission to improve lives,{" "}
              <strong>Harmony Rise Homecare</strong> began as a response to the
              growing need for reliable and compassionate nonmedical care in our
              community.
            </p>
            <div className="about-intro-tags">
              <span className="hr-chip">
                <FontAwesomeIcon icon={faCheck} /> Nonmedical In-Home Care
              </span>
              <span className="hr-chip">
                <FontAwesomeIcon icon={faCheck} /> Independence First
              </span>
              <span className="hr-chip">
                <FontAwesomeIcon icon={faCheck} /> Community Focused
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="hr-section bg-cream">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Our Purpose</span>
            <h2 className="hr-heading">Guided by Mission and Vision</h2>
          </div>
          <div className="mv-grid" style={{ marginTop: "50px" }}>
            <div className="mv-card reveal">
              <div className="mv-icon">
                <FontAwesomeIcon icon={faBullseye} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To enhance lives through dedicated nonmedical in-home care,
                fostering independence and improving quality of life for every
                client with compassion and respect.
              </p>
            </div>
            <div className="mv-card reveal">
              <div className="mv-icon">
                <FontAwesomeIcon icon={faEye} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To be the most trusted homecare partner in our community, known
                for personalized, compassionate care that empowers clients to
                live with dignity and comfort at home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="hr-section">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Core Values</span>
            <h2 className="hr-heading">
              Our Principles Guide Our Care Services
            </h2>
          </div>
          <div className="values-grid">
            {values.map((v) => (
              <div className="value-card reveal" key={v.title}>
                <div className="value-icon">
                  <FontAwesomeIcon icon={v.icon} />
                </div>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="hr-section bg-mint">
        <div className="why-choose hr-container">
          <div className="reveal">
            <span className="hr-eyebrow">Why Choose Us</span>
            <h2 className="hr-heading">
              A Partner You Can <span className="accent">Trust</span>
            </h2>
            <div className="why-list">
              {whyPoints.map((w) => (
                <div className="why-list-item" key={w.title}>
                  <span className="wl-icon">
                    <FontAwesomeIcon icon={w.icon} />
                  </span>
                  <div>
                    <h4>{w.title}</h4>
                    <p>{w.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="why-choose-visual reveal">
            <img
              src={whyImg}
              alt="Caregivers assisting an elderly man"
              className="wcv-photo"
            />
            <h3>Care That Feels Like Family</h3>
            <p>
              We go beyond expectations to make every client feel supported,
              respected and at home. Let us be part of your care journey.
            </p>
            <Link to="/contact" className="hr-btn hr-btn-white">
              Connect With Us <FontAwesomeIcon icon={faPhone} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hr-section" style={{ paddingTop: 0 }}>
        <div className="hr-container">
          <div className="cta-band reveal">
            <h2>Ready to Care?</h2>
            <p>Connect with us for personalized support tailored to you.</p>
            <div className="cta-actions">
              <Link to="/book-appointment" className="hr-btn hr-btn-white hr-btn-lg">
                <FontAwesomeIcon icon={faCalendarCheck} /> Book An Appointment
              </Link>
              <a href="tel:2535230098" className="hr-btn hr-btn-ghost hr-btn-lg">
                <FontAwesomeIcon icon={faPhone} /> Call: 253-523-0098
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default About;
